import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Card, Space, App } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedTransportForm from '../components/SharedTransportForm';
import { normalizeOrderOptions, type OrderOptionDto } from '../dto/order';
import { canManageTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { mapTransportMutationErrorToMessage } from '../modules/transports/transportRouteViews';
import api from '../services/api';
import { authService } from '../services/auth';
import { createSplitTransportRoutes } from '../modules/transports/api/transportRoutesApi';
import './TransportRouteFormPage.css';

const CreateTransportRoute: React.FC = () => {
  const { t } = useTranslation(['transport', 'common']);
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetOrderId = searchParams.get('orderId') || undefined;
  const initialOrderIds = useMemo(
    () => (presetOrderId ? [presetOrderId] : undefined),
    [presetOrderId],
  );
  const [availableOrders, setAvailableOrders] = useState<OrderOptionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const currentUser = authService.getCurrentUser();
  const canManageTransports = canManageTransportRoutes(currentUser);
  const canEditTransportCosts = authService.canEditTransportCosts();
  const canViewTransportCosts = authService.canViewTransportCosts();

  const fetchOrders = useCallback(async () => {
    try {
      // Ask for supplier purchase-value hints so the cargo "Wartość towaru"
      // column can prefill from the linked supplier orders. The backend strips
      // the hints for users without supplier-order purchase-price visibility, so
      // requesting them unconditionally is safe.
      const response = await api.get('/orders', {
        params: { includeSupplierPurchaseHints: 'true' },
      });
      setAvailableOrders(normalizeOrderOptions(response.data, 'transport.create.availableOrders'));
    } catch (error) {
      message.error(t('transport:errorFetchingOrders'));
    }
  }, [t]);

  useEffect(() => {
    if (!canManageTransports) {
      navigate('/app/transports', { replace: true });
      return;
    }

    void fetchOrders();
  }, [canManageTransports, fetchOrders, navigate]);

  const handleSubmit = async (payload: Record<string, unknown>) => {
    setLoading(true);

    try {
      const response = await api.post('/transport-routes', payload);
      message.success(t('transport:routeCreatedSuccessfully'));
      navigate(`/app/transports/${response.data.id}`);
    } catch (error: any) {
      // MED (i18n error mapping): translate stable backend codes (e.g.
      // MIXED_UNIT_QUANTITY_ALLOCATION) into Polish-first copy instead of
      // echoing the raw English backend message — single home in
      // transportRouteViews.mapTransportMutationErrorToMessage.
      const mapped = mapTransportMutationErrorToMessage(t, error?.response?.data);
      message.error(mapped || error?.response?.data?.error || t('transport:errorCreatingRoute'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSplit = async (payload: Record<string, unknown>) => {
    setLoading(true);

    try {
      const response = await createSplitTransportRoutes(payload);
      message.success(t('transport:split.created', { count: response?.count || 0 }));
      navigate('/app/transports');
    } catch (error: any) {
      const mapped = mapTransportMutationErrorToMessage(t, error?.response?.data);
      message.error(mapped || error?.response?.data?.error || t('transport:errorCreatingRoute'));
    } finally {
      setLoading(false);
    }
  };

  if (!canManageTransports) {
    return null;
  }

  return (
    <div className="transport-route-form-page">
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          {t('common:back')}
        </Button>
      </Space>

      <Card className="transport-route-form-shell-card" title={t('transport:createNewRoute')}>
        <SharedTransportForm
          availableOrders={availableOrders}
          initialOrderIds={initialOrderIds}
          canEditTransportCosts={canEditTransportCosts}
          canViewTransportCosts={canViewTransportCosts}
          loading={loading}
          onCancel={() => navigate(-1)}
          onSubmit={handleSubmit}
          onSubmitSplit={handleSubmitSplit}
        />
      </Card>
    </div>
  );
};

export default CreateTransportRoute;
